/**
 * LIABILITY ENGINE
 * 
 * Core service for deterministic liability determination, time-bound matching,
 * and workflow routing.
 * 
 * Key Features:
 * - Rule-based liability logic (L001-L007)
 * - Time-bound hire matching with late return detection
 * - Evidence tier calculation
 * - Risk scoring and guardrails
 * - Workflow routing recommendations
 */

import { supabase } from './supabaseClient';

// =====================================================
// CONFIGURATION
// =====================================================

/**
 * Grace period configuration for late returns
 *
 * CRITICAL: These values must be consistent across all functions
 * to avoid mismatched penalties and auto-assignment decisions.
 */
export const GRACE_PERIOD_CONFIG = {
  // Maximum hours after hire end to still match as "late_return"
  MATCHING_GRACE_HOURS: 24,

  // Threshold for "within_grace" vs "beyond_grace" penalty
  // Tickets returned within this period get -10 penalty
  // Tickets returned beyond this period get -25 penalty
  PENALTY_GRACE_HOURS: 1,

  // For display/logging
  DISPLAY_GRACE_HOURS: 1
} as const;

// =====================================================
// TYPES
// =====================================================

export type LiabilityResult = 
  | 'fleet' 
  | 'driver' 
  | 'driver_internal' 
  | 'hire_company' 
  | 'unknown' 
  | 'pending_verification' 
  | 'escalate';

export type HireMatchType = 'exact' | 'late_return' | 'early_return' | 'gap' | 'none';
export type LateReturnSeverity = 'minor' | 'moderate' | 'severe';
export type RecommendedAction = 'transfer' | 'appeal' | 'pay' | 'review' | 'investigate';
export type AbsorptionReason = 'grace_period' | 'goodwill' | 'admin_error' | 'below_threshold' | 'first_offense';

export interface LiabilityEvidence {
  type: string;
  description: string;
  weight: number;
  verified: boolean;
}

export interface TimeMatch {
  hire: any;
  match_type: HireMatchType;
  overlap_minutes: number;
  confidence: number;
}

export interface LateReturnResult {
  detected: boolean;
  minutes: number;
  severity: LateReturnSeverity;
  grace_period_applied: boolean;
}

export interface RiskScore {
  total: number;
  evidence_score: number;
  authority_score: number;
  stage_score: number;
  amount_score: number;
  factors: string[];
}

export interface LiabilityDecision {
  // Legal liability (factual)
  legal_result: LiabilityResult;
  legal_confidence: number;
  legal_reason_code: string;
  legal_reason_text: string;
  evidence_tier: 1 | 2 | 3 | 4 | 5;

  // Commercial liability (policy-based)
  commercial_result?: 'fleet' | 'driver' | 'hire_company' | 'shared';
  commercial_reason?: string;

  // Policy absorption
  absorbed_by_policy: boolean;
  absorption_reason?: AbsorptionReason;
  absorption_amount?: number;

  // Safety flags
  formal_submission_safe: boolean;
  verification_required: boolean;
  escalation_required: boolean;

  // Matched hire
  matched_hire?: any;
  hire_match_type: HireMatchType;
  hire_overlap_minutes?: number;

  // Late return
  late_return?: LateReturnResult;

  // Workflow routing
  recommended_action: RecommendedAction;
  assigned_workflow?: string;
  assigned_team?: string;

  // Risk scoring
  risk_score: RiskScore;

  // Evidence
  evidence: LiabilityEvidence[];
}

// =====================================================
// AUDIT AGREEMENT TYPES (Single Source of Truth)
// =====================================================

export type AuditMatchType = 'exact' | 'overlap' | 'late_return' | 'none' | 'multiple';
export type LateReturnStatus = 'none' | 'within_grace' | 'beyond_grace';
export type NoHireReason =
  | 'hire_not_yet_uploaded'
  | 'vehicle_company_owned'
  | 'third_party_error'
  | 'other';

export type OverrideReason =
  | 'wrong_hire_detected'
  | 'hire_dates_incorrect'
  | 'manual_assignment_preferred'
  | 'system_error'
  | 'other';

/**
 * AuditResult - The single source of truth for hire detection and evidence evaluation
 *
 * This is the output of auditAgreement() and feeds directly into:
 * - Evidence tier calculation
 * - Auto-assignment decision
 * - Automation eligibility
 */
export interface AuditResult {
  // Hire detection
  hireDetected: boolean;
  hireId?: string;
  matchType: AuditMatchType;

  // Agreement status
  agreementPresent: boolean;
  agreementSigned: boolean;
  liabilityClausePresent: boolean;
  coversContravention: boolean;

  // Late return
  lateReturnStatus: LateReturnStatus;
  lateReturnMinutes?: number;

  // Evidence and confidence
  evidenceTier: 1 | 2 | 3 | 4 | 5;
  confidenceScore: number; // 0-100

  // Auto-assignment eligibility
  autoAssignable: boolean;

  // Explanation for UI
  reason: string;
  blockingFactors: string[];
  warningFactors?: string[];

  // Conflicting hires (if matchType === 'multiple')
  conflictingHires?: Array<{
    hireId: string;
    driverName: string;
    startDate: string;
    endDate?: string;
  }>;
}

/**
 * ManualOverrideEvent - Immutable audit log entry for override tracking
 */
export interface ManualOverrideEvent {
  ticketId: string;
  previousHireId: string | null;
  newHireId: string | null;
  overriddenByUserId: string;
  reason: OverrideReason;
  reasonNote?: string;
  timestamp: string;
  previousConfidenceScore: number;
  previousAuditResult?: AuditResult;
}

/**
 * NoHireConfirmation - Explicit confirmation that no hire exists
 */
export interface NoHireConfirmation {
  ticketId: string;
  confirmedByUserId: string;
  reason: NoHireReason;
  reasonNote?: string;
  timestamp: string;
  // After confirmation, upgrades to L001a (confirmed) with Tier 2 evidence
}

/**
 * Confidence scoring breakdown
 */
export interface ConfidenceBreakdown {
  exactHireMatch: number;      // +40 for single exact match
  datesCoverContravention: number; // +20 for full coverage
  agreementSigned: number;     // +15 for signed agreement
  liabilityClausePresent: number; // +15 for liability clause
  noConflictingHires: number;  // +10 for no conflicts
  lateReturnPenalty: number;   // -10 within grace, -25 beyond grace
  total: number;
}

// Confidence thresholds
export const CONFIDENCE_THRESHOLDS = {
  AUTO_ASSIGN: 85,    // ≥85 → Auto-assign allowed
  SUGGEST: 70,        // 70-84 → Suggest, require review
  MANUAL_ONLY: 0      // <70 → Manual only
} as const;

// =====================================================
// CORE FUNCTIONS
// =====================================================

/**
 * AUDIT AGREEMENT - The deterministic, automatic evidence evaluator
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - Hire detection
 * - Evidence tier calculation
 * - Auto-assignment eligibility
 *
 * Detection ≠ Commitment: This function detects and evaluates,
 * but does NOT modify the ticket. Commitment happens in the caller.
 */
export async function auditAgreement(
  ticketId: string,
  organisationId: string
): Promise<AuditResult> {

  // 1. Fetch ticket data
  const { data: ticket, error: ticketError } = await supabase
    .from('tickets')
    .select('*')
    .eq('id', ticketId)
    .single();

  if (ticketError || !ticket) {
    throw new Error('Ticket not found');
  }

  // 2. Fetch all hires for this vehicle
  const hires = await getAllHiresForVehicle(ticket.vehicle_id, organisationId);

  // 3. Find matching hires at contravention time
  const contraventionDate = new Date(ticket.contravention_date);
  const matchingHires = findAllMatchingHires(ticket.vehicle_id, contraventionDate, hires);

  // 4. Calculate confidence and build result
  return buildAuditResult(ticket, matchingHires, contraventionDate);
}

/**
 * Find ALL hires that could match (not just the best one)
 * This is important for detecting conflicts
 */
function findAllMatchingHires(
  vehicleId: string,
  contraventionDate: Date,
  hires: any[]
): Array<{ hire: any; matchType: AuditMatchType; lateMinutes: number }> {

  if (!hires || hires.length === 0) {
    return [];
  }

  const vehicleHires = hires.filter(h => h.vehicle_id === vehicleId);
  const matches: Array<{ hire: any; matchType: AuditMatchType; lateMinutes: number }> = [];

  for (const hire of vehicleHires) {
    const start = new Date(hire.start_date);
    const end = hire.end_date ? new Date(hire.end_date) : null;

    // Exact match: contravention within hire period
    if (contraventionDate >= start && (!end || contraventionDate <= end)) {
      matches.push({ hire, matchType: 'exact', lateMinutes: 0 });
      continue;
    }

    // Late return: contravention after hire end, within grace period
    if (end && contraventionDate > end) {
      const minutesLate = Math.floor((contraventionDate.getTime() - end.getTime()) / 60000);
      const hoursLate = minutesLate / 60;

      if (hoursLate <= GRACE_PERIOD_CONFIG.MATCHING_GRACE_HOURS) {
        matches.push({ hire, matchType: 'late_return', lateMinutes: minutesLate });
        continue;
      }
    }

    // Early return: contravention before hire start, within 2h (early pickup)
    if (contraventionDate < start) {
      const minutesEarly = Math.floor((start.getTime() - contraventionDate.getTime()) / 60000);
      const hoursEarly = minutesEarly / 60;

      if (hoursEarly <= 2) {
        matches.push({ hire, matchType: 'overlap', lateMinutes: 0 });
      }
    }
  }

  return matches;
}

/**
 * Build the AuditResult with confidence scoring
 */
function buildAuditResult(
  _ticket: any,
  matchingHires: Array<{ hire: any; matchType: AuditMatchType; lateMinutes: number }>,
  contraventionDate: Date
): AuditResult {

  const blockingFactors: string[] = [];
  const warningFactors: string[] = [];

  // No hires found
  if (matchingHires.length === 0) {
    return {
      hireDetected: false,
      matchType: 'none',
      agreementPresent: false,
      agreementSigned: false,
      liabilityClausePresent: false,
      coversContravention: false,
      lateReturnStatus: 'none',
      evidenceTier: 5,
      confidenceScore: 60,
      autoAssignable: false,
      reason: 'No hire record found for this vehicle at contravention time',
      blockingFactors: ['No hire detected - confirmation required'],
      warningFactors
    };
  }

  // Multiple conflicting hires
  if (matchingHires.length > 1) {
    const conflicts = matchingHires.map(m => ({
      hireId: m.hire.id,
      driverName: m.hire.driver_name || 'Unknown',
      startDate: m.hire.start_date,
      endDate: m.hire.end_date
    }));

    return {
      hireDetected: true,
      matchType: 'multiple',
      agreementPresent: false,
      agreementSigned: false,
      liabilityClausePresent: false,
      coversContravention: false,
      lateReturnStatus: 'none',
      evidenceTier: 4,
      confidenceScore: 40,
      autoAssignable: false,
      reason: `Multiple conflicting hires found (${matchingHires.length})`,
      blockingFactors: ['Multiple conflicting hires - manual resolution required'],
      warningFactors,
      conflictingHires: conflicts
    };
  }

  // Single match - calculate confidence
  const match = matchingHires[0];
  const hire = match.hire;

  // Validate hire data before proceeding
  if (!hire || !hire.id || !hire.start_date) {
    return {
      hireDetected: false,
      matchType: 'none',
      agreementPresent: false,
      agreementSigned: false,
      liabilityClausePresent: false,
      coversContravention: false,
      lateReturnStatus: 'none',
      evidenceTier: 5,
      confidenceScore: 0,
      autoAssignable: false,
      reason: 'Invalid hire data - missing required fields',
      blockingFactors: ['Hire data validation failed'],
      warningFactors
    };
  }

  // Validate driver information exists
  if (!hire.driver_name || !hire.driver_email) {
    warningFactors.push('Missing driver contact information');
  }

  // Calculate confidence breakdown
  const breakdown = calculateConfidenceBreakdown(hire, match, contraventionDate);

  // Determine late return status
  let lateReturnStatus: LateReturnStatus = 'none';
  if (match.matchType === 'late_return') {
    const hoursLate = match.lateMinutes / 60;
    lateReturnStatus = hoursLate <= GRACE_PERIOD_CONFIG.PENALTY_GRACE_HOURS ? 'within_grace' : 'beyond_grace';
  }

  // Check agreement status
  const agreementPresent = !!hire.agreement_url;
  const agreementSigned = !!hire.agreement_signed_at;
  const liabilityClausePresent = !!hire.agreement_has_liability_clause;

  // Check if dates cover contravention
  const start = new Date(hire.start_date);
  const end = hire.end_date ? new Date(hire.end_date) : null;
  const coversContravention = contraventionDate >= start && (!end || contraventionDate <= end);

  // Calculate evidence tier
  const evidenceTier = calculateEvidenceTierFromAudit(
    agreementPresent,
    agreementSigned,
    liabilityClausePresent,
    lateReturnStatus
  );

  // Build blocking factors
  if (!agreementPresent) {
    blockingFactors.push('No hire agreement uploaded');
  }
  if (!liabilityClausePresent && agreementPresent) {
    blockingFactors.push('Liability clause not confirmed');
  }
  if (lateReturnStatus === 'beyond_grace') {
    blockingFactors.push('Late return beyond grace period');
  }
  if (breakdown.total < CONFIDENCE_THRESHOLDS.AUTO_ASSIGN) {
    blockingFactors.push(`Confidence score ${breakdown.total}% below auto-assign threshold (${CONFIDENCE_THRESHOLDS.AUTO_ASSIGN}%)`);
  }

  // Determine auto-assignability
  const autoAssignable =
    breakdown.total >= CONFIDENCE_THRESHOLDS.AUTO_ASSIGN &&
    blockingFactors.length === 0;

  // Build reason string
  let reason = '';
  if (autoAssignable) {
    reason = `High confidence match (${breakdown.total}%) with verified hire agreement`;
  } else if (breakdown.total >= CONFIDENCE_THRESHOLDS.SUGGEST) {
    reason = `Hire match found (${breakdown.total}%) - review recommended`;
  } else {
    reason = `Weak hire match (${breakdown.total}%) - manual assignment required`;
  }

  return {
    hireDetected: true,
    hireId: hire.id,
    matchType: match.matchType === 'exact' ? 'exact' :
               match.matchType === 'late_return' ? 'late_return' : 'overlap',
    agreementPresent,
    agreementSigned,
    liabilityClausePresent,
    coversContravention,
    lateReturnStatus,
    lateReturnMinutes: match.lateMinutes || undefined,
    evidenceTier,
    confidenceScore: breakdown.total,
    autoAssignable,
    reason,
    blockingFactors,
    warningFactors
  };
}

/**
 * Calculate confidence score breakdown
 *
 * Scoring:
 * - Exact hire match (single hire): +40
 * - Hire dates fully cover contravention: +20
 * - Signed agreement: +15
 * - Liability clause present: +15
 * - No conflicting hires: +10
 * - Late return within grace: -10
 * - Late return beyond grace: -25
 */
function calculateConfidenceBreakdown(
  hire: any,
  match: { matchType: AuditMatchType; lateMinutes: number },
  contraventionDate: Date
): ConfidenceBreakdown {

  let exactHireMatch = 0;
  let datesCoverContravention = 0;
  let agreementSigned = 0;
  let liabilityClausePresent = 0;
  let noConflictingHires = 10; // Single match, so no conflicts
  let lateReturnPenalty = 0;

  // +40 for exact match
  if (match.matchType === 'exact') {
    exactHireMatch = 40;
  } else if (match.matchType === 'overlap') {
    exactHireMatch = 30; // Partial credit for overlap
  } else if (match.matchType === 'late_return') {
    exactHireMatch = 25; // Partial credit for late return
  }

  // +20 for dates covering contravention
  const start = new Date(hire.start_date);
  const end = hire.end_date ? new Date(hire.end_date) : null;
  if (contraventionDate >= start && (!end || contraventionDate <= end)) {
    datesCoverContravention = 20;
  }

  // +15 for signed agreement
  if (hire.agreement_signed_at) {
    agreementSigned = 15;
  } else if (hire.agreement_url) {
    agreementSigned = 10; // Partial credit for uploaded but unsigned
  }

  // +15 for liability clause
  if (hire.agreement_has_liability_clause) {
    liabilityClausePresent = 15;
  }

  // Late return penalties
  if (match.matchType === 'late_return') {
    const hoursLate = match.lateMinutes / 60;
    if (hoursLate <= GRACE_PERIOD_CONFIG.PENALTY_GRACE_HOURS) {
      lateReturnPenalty = -10; // Within grace
    } else {
      lateReturnPenalty = -25; // Beyond grace
    }
  }

  const total = Math.max(0, Math.min(100,
    exactHireMatch +
    datesCoverContravention +
    agreementSigned +
    liabilityClausePresent +
    noConflictingHires +
    lateReturnPenalty
  ));

  return {
    exactHireMatch,
    datesCoverContravention,
    agreementSigned,
    liabilityClausePresent,
    noConflictingHires,
    lateReturnPenalty,
    total
  };
}

/**
 * Calculate evidence tier from audit results
 */
function calculateEvidenceTierFromAudit(
  agreementPresent: boolean,
  agreementSigned: boolean,
  liabilityClausePresent: boolean,
  lateReturnStatus: LateReturnStatus
): 1 | 2 | 3 | 4 | 5 {

  // Tier 1: Signed agreement + liability clause
  if (agreementPresent && agreementSigned && liabilityClausePresent) {
    // Late return beyond grace caps at Tier 2
    if (lateReturnStatus === 'beyond_grace') {
      return 2;
    }
    return 1;
  }

  // Tier 2: E-signature with audit trail OR agreement without liability clause confirmed
  if (agreementPresent && agreementSigned) {
    return 2;
  }

  // Tier 3: Hire record only (no signature)
  if (agreementPresent) {
    return 3;
  }

  // Tier 4: Pool vehicle with sign-out log (we check for assignment_type)
  // For now, treat all non-agreement hires as Tier 4

  return 4;
}

/**
 * Determine liability for a ticket
 */
/**
 * Extended result that includes both decision and audit
 */
export interface LiabilityAnalysisResult {
  decision: LiabilityDecision;
  audit: AuditResult;
  autoAssigned: boolean;
}

/**
 * Determine liability for a ticket
 *
 * NOW USES auditAgreement() as the single source of truth for hire detection.
 * Returns both the liability decision AND the audit result.
 */
export async function determineLiability(
  ticketId: string,
  organisationId: string,
  forceRecalculate: boolean = false
): Promise<LiabilityAnalysisResult> {

  // 1. Check for existing decision (if not forcing recalculate)
  if (!forceRecalculate) {
    const existing = await getExistingDecision(ticketId);
    if (existing) {
      // Still run audit for fresh data, but use existing decision
      const audit = await auditAgreement(ticketId, organisationId);
      return {
        decision: existing,
        audit,
        autoAssigned: false // Existing decision, not auto-assigned now
      };
    }
  }

  // 2. Run auditAgreement - THE SINGLE SOURCE OF TRUTH
  const audit = await auditAgreement(ticketId, organisationId);

  // 3. Fetch ticket data
  const { data: ticket, error: ticketError } = await supabase
    .from('tickets')
    .select('*')
    .eq('id', ticketId)
    .single();

  if (ticketError || !ticket) {
    throw new Error('Ticket not found');
  }

  // 4. Fetch related data based on audit results
  const [hire, driver, hires] = await Promise.all([
    audit.hireId ? getHire(audit.hireId) : (ticket.hire_id ? getHire(ticket.hire_id) : null),
    ticket.driver_id ? getDriver(ticket.driver_id) : null,
    getAllHiresForVehicle(ticket.vehicle_id, organisationId)
  ]);

  // 5. Convert audit matchType to TimeMatch for compatibility
  const timeMatch = audit.hireDetected && audit.hireId
    ? convertAuditToTimeMatch(audit, hire)
    : findHireAtTime(ticket.vehicle_id, new Date(ticket.contravention_date), hires);

  // 6. Late return detection
  const lateReturn = audit.lateReturnStatus !== 'none' && hire
    ? detectLateReturn(hire, new Date(ticket.contravention_date))
    : null;

  // 7. Apply liability rules (uses audit.evidenceTier)
  const decision = applyLiabilityRulesWithAudit(ticket, timeMatch, lateReturn, hire, driver, audit);

  // 8. Calculate risk score
  const riskScore = calculateRiskScore(ticket, decision, timeMatch);

  // 9. Save decision to database (includes audit result)
  await saveLiabilityDecision(ticketId, organisationId, {
    ...decision,
    risk_score: riskScore
  }, audit);

  const fullDecision: LiabilityDecision = {
    ...decision,
    risk_score: riskScore
  };

  return {
    decision: fullDecision,
    audit,
    autoAssigned: false // Caller decides whether to auto-assign
  };
}

/**
 * Convert AuditResult to TimeMatch for backwards compatibility
 */
function convertAuditToTimeMatch(audit: AuditResult, hire: any): TimeMatch | null {
  if (!audit.hireDetected || !hire) {
    return null;
  }

  let matchType: HireMatchType;
  switch (audit.matchType) {
    case 'exact':
      matchType = 'exact';
      break;
    case 'late_return':
      matchType = 'late_return';
      break;
    case 'overlap':
      matchType = 'early_return';
      break;
    default:
      matchType = 'none';
  }

  return {
    hire,
    match_type: matchType,
    overlap_minutes: audit.lateReturnMinutes || 0,
    confidence: audit.confidenceScore
  };
}

/**
 * Apply liability rules WITH audit data
 * This uses the audit's evidence tier instead of recalculating
 */
function applyLiabilityRulesWithAudit(
  ticket: any,
  timeMatch: TimeMatch | null,
  lateReturn: LateReturnResult | null,
  hire: any | null,
  driver: any | null,
  audit: AuditResult
): Omit<LiabilityDecision, 'risk_score'> {
  // Use the existing applyLiabilityRules but override evidence_tier from audit
  const baseDecision = applyLiabilityRules(ticket, timeMatch, lateReturn, hire, driver);

  // Override evidence tier with audit's calculation (single source of truth)
  return {
    ...baseDecision,
    evidence_tier: audit.evidenceTier,
    // Update confidence to match audit
    legal_confidence: Math.max(baseDecision.legal_confidence, audit.confidenceScore)
  };
}

/**
 * Find hire at specific time with late return detection
 */
export function findHireAtTime(
  vehicleId: string,
  contraventionDate: Date,
  hires: any[]
): TimeMatch | null {

  if (!hires || hires.length === 0) {
    return null;
  }

  // Filter hires for this vehicle
  const vehicleHires = hires.filter(h => h.vehicle_id === vehicleId);

  if (vehicleHires.length === 0) {
    return null;
  }

  // Find exact match first
  for (const hire of vehicleHires) {
    const start = new Date(hire.start_date);
    const end = hire.end_date ? new Date(hire.end_date) : null;

    // Exact match: contravention within hire period
    if (contraventionDate >= start && (!end || contraventionDate <= end)) {
      const overlapMinutes = end
        ? Math.floor((end.getTime() - contraventionDate.getTime()) / 60000)
        : 999999;

      return {
        hire,
        match_type: 'exact',
        overlap_minutes: overlapMinutes,
        confidence: 100
      };
    }
  }

  // Check for late return (contravention after hire end, within grace period)
  for (const hire of vehicleHires) {
    const end = hire.end_date ? new Date(hire.end_date) : null;

    if (end && contraventionDate > end) {
      const minutesLate = Math.floor((contraventionDate.getTime() - end.getTime()) / 60000);
      const hoursLate = minutesLate / 60;

      if (hoursLate <= GRACE_PERIOD_CONFIG.MATCHING_GRACE_HOURS) {
        return {
          hire,
          match_type: 'late_return',
          overlap_minutes: -minutesLate,
          confidence: Math.max(50, 100 - (hoursLate * 10))
        };
      }
    }
  }

  // Check for early arrival (contravention before hire start, within grace period)
  for (const hire of vehicleHires) {
    const start = new Date(hire.start_date);

    if (contraventionDate < start) {
      const minutesEarly = Math.floor((start.getTime() - contraventionDate.getTime()) / 60000);
      const hoursEarly = minutesEarly / 60;

      if (hoursEarly <= 2) { // 2 hour grace for early pickup
        return {
          hire,
          match_type: 'early_return',
          overlap_minutes: -minutesEarly,
          confidence: Math.max(40, 100 - (hoursEarly * 20))
        };
      }
    }
  }

  // No match found
  return {
    hire: vehicleHires[0], // Return closest hire for reference
    match_type: 'gap',
    overlap_minutes: 0,
    confidence: 0
  };
}

/**
 * Detect late return and calculate severity
 *
 * IMPORTANT: All date comparisons are done in UTC to avoid timezone issues.
 * This prevents misclassification by ±1 hour depending on local timezone.
 */
export function detectLateReturn(
  hire: any,
  contraventionDate: Date
): LateReturnResult {

  if (!hire.end_date) {
    return {
      detected: false,
      minutes: 0,
      severity: 'minor',
      grace_period_applied: false
    };
  }

  // Normalize to UTC to avoid timezone comparison issues
  // This ensures consistent behavior regardless of server/client timezone
  const end = new Date(hire.end_date);
  const endUTC = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(),
                          end.getUTCHours(), end.getUTCMinutes(), end.getUTCSeconds());
  const contraventionUTC = Date.UTC(contraventionDate.getUTCFullYear(), contraventionDate.getUTCMonth(),
                                    contraventionDate.getUTCDate(), contraventionDate.getUTCHours(),
                                    contraventionDate.getUTCMinutes(), contraventionDate.getUTCSeconds());

  if (contraventionUTC <= endUTC) {
    return {
      detected: false,
      minutes: 0,
      severity: 'minor',
      grace_period_applied: false
    };
  }

  const minutesLate = Math.floor((contraventionUTC - endUTC) / 60000);
  const hoursLate = minutesLate / 60;

  // Determine severity
  let severity: LateReturnSeverity = 'minor';
  if (hoursLate > 12) {
    severity = 'severe';
  } else if (hoursLate > 4) {
    severity = 'moderate';
  }

  // Grace period: configurable threshold for minor returns
  const gracePeriodApplied = hoursLate <= GRACE_PERIOD_CONFIG.DISPLAY_GRACE_HOURS;

  return {
    detected: true,
    minutes: minutesLate,
    severity,
    grace_period_applied: gracePeriodApplied
  };
}

/**
 * Apply liability rules based on ticket data and matches
 */
export function applyLiabilityRules(
  ticket: any,
  timeMatch: TimeMatch | null,
  lateReturn: LateReturnResult | null,
  hire: any | null,
  _driver: any | null
): Omit<LiabilityDecision, 'risk_score'> {

  const evidence: LiabilityEvidence[] = [];
  let legal_result: LiabilityResult = 'unknown';
  let legal_confidence = 0;
  let legal_reason_code = '';
  let legal_reason_text = '';
  let evidence_tier: 1 | 2 | 3 | 4 | 5 = 5;
  let verification_required = false;
  let escalation_required = false;
  let formal_submission_safe = true;
  let absorbed_by_policy = false;
  let absorption_reason: AbsorptionReason | undefined;
  let recommended_action: RecommendedAction = 'review';

  // =====================================================
  // PRIORITY 1: Check for exact hire match first
  // =====================================================

  // RULE L002: Hire + signed agreement + liability clause (Tier 1)
  if (timeMatch && timeMatch.match_type === 'exact' && hire?.agreement_url && hire?.agreement_has_liability_clause) {
    legal_result = 'driver';
    legal_confidence = 100;
    legal_reason_code = 'L002';
    legal_reason_text = 'Verified hire with signed liability agreement at time of contravention';
    evidence_tier = 1;
    recommended_action = 'transfer';
    formal_submission_safe = true;

    evidence.push({
      type: 'hire_agreement',
      description: 'Signed hire agreement with liability clause',
      weight: 40,
      verified: true
    });

    evidence.push({
      type: 'time_match',
      description: 'Exact time match with hire period',
      weight: 30,
      verified: true
    });
  }

  // RULE L003: Hire + e-signature with audit trail (Tier 2)
  // Note: Currently we don't have e-signature tracking, so this falls through to L004
  else if (timeMatch && timeMatch.match_type === 'exact' && hire?.agreement_url && !hire?.agreement_has_liability_clause) {
    legal_result = 'driver';
    legal_confidence = 80;
    legal_reason_code = 'L003';
    legal_reason_text = 'Hire agreement exists but liability clause not confirmed - review recommended';
    evidence_tier = 2;
    recommended_action = 'review';
    formal_submission_safe = true;

    evidence.push({
      type: 'hire_agreement',
      description: 'Hire agreement uploaded (liability clause unconfirmed)',
      weight: 30,
      verified: true
    });

    evidence.push({
      type: 'time_match',
      description: 'Exact time match with hire period',
      weight: 30,
      verified: true
    });
  }

  // RULE L004: Hire + no signature (Tier 3) - Internal only
  else if (timeMatch && timeMatch.match_type === 'exact' && hire && !hire?.agreement_url) {
    legal_result = 'driver_internal';
    legal_confidence = 70;
    legal_reason_code = 'L004';
    legal_reason_text = 'Hire match found but agreement not uploaded - internal billing only';
    evidence_tier = 3;
    verification_required = true;
    recommended_action = 'review';
    formal_submission_safe = false;

    evidence.push({
      type: 'time_match',
      description: 'Time match with hire period (no documentation)',
      weight: 30,
      verified: false
    });
  }

  // =====================================================
  // PRIORITY 2: Check for late return scenarios
  // =====================================================

  // RULE L008: Late return - within grace period
  else if (lateReturn && lateReturn.detected && lateReturn.grace_period_applied) {
    // Determine evidence tier based on hire agreement
    const hasTier1Evidence = hire?.agreement_url && hire?.agreement_has_liability_clause;

    legal_result = 'driver';
    legal_confidence = 80;
    legal_reason_code = 'L008';
    legal_reason_text = `Late return (${Math.floor(lateReturn.minutes / 60)}h ${lateReturn.minutes % 60}m) within grace period - driver liable`;
    evidence_tier = hasTier1Evidence ? 2 : 3;
    recommended_action = 'transfer';
    absorbed_by_policy = true;
    absorption_reason = 'grace_period';
    formal_submission_safe = hasTier1Evidence;

    evidence.push({
      type: 'late_return',
      description: `Late return: ${lateReturn.minutes} minutes (within grace period)`,
      weight: 25,
      verified: true
    });
  }

  // RULE L009/L010: Late return - beyond grace period
  else if (lateReturn && lateReturn.detected && !lateReturn.grace_period_applied) {
    const hasTier1or2Evidence = hire?.agreement_url;

    if (hasTier1or2Evidence) {
      // L009: Tier 1-2 evidence
      legal_result = 'driver';
      legal_confidence = 95;
      legal_reason_code = 'L009';
      legal_reason_text = `Late return (${Math.floor(lateReturn.minutes / 60)}h ${lateReturn.minutes % 60}m) beyond grace period - driver liable`;
      evidence_tier = hire?.agreement_has_liability_clause ? 1 : 2;
      recommended_action = 'transfer';
      formal_submission_safe = true;
    } else {
      // L010: Tier 3+ evidence - internal only
      legal_result = 'driver_internal';
      legal_confidence = 75;
      legal_reason_code = 'L010';
      legal_reason_text = `Late return (${Math.floor(lateReturn.minutes / 60)}h ${lateReturn.minutes % 60}m) beyond grace period - weak evidence, internal only`;
      evidence_tier = 3;
      recommended_action = 'review';
      formal_submission_safe = false;
    }

    evidence.push({
      type: 'late_return',
      description: `Significant late return: ${lateReturn.minutes} minutes`,
      weight: 20,
      verified: true
    });
  }

  // =====================================================
  // PRIORITY 3: No hire match scenarios
  // =====================================================

  // RULE L001a/L001b: No hire record
  else if (!timeMatch || timeMatch.match_type === 'gap' || timeMatch.match_type === 'none') {
    // For now, we treat all "no hire" as confirmed (L001a)
    // In future, add verification workflow for L001b
    legal_result = 'fleet';
    legal_confidence = 95;
    legal_reason_code = 'L001a';
    legal_reason_text = 'No hire found at time of contravention - fleet retains liability';
    evidence_tier = 2; // Tier 2 for confirmed fleet liability
    recommended_action = 'appeal';
    formal_submission_safe = true;

    evidence.push({
      type: 'no_hire',
      description: 'No hire agreement at time of contravention',
      weight: 35,
      verified: true
    });
  }

  // RULE L006: Driver assigned but no hire agreement
  else if (ticket.driver_id && !hire) {
    legal_result = 'pending_verification';
    legal_confidence = 60;
    legal_reason_code = 'L006';
    legal_reason_text = 'Driver assigned but no hire agreement - verification required';
    evidence_tier = 4;
    verification_required = true;
    recommended_action = 'review';
    formal_submission_safe = false;

    evidence.push({
      type: 'driver_assigned',
      description: 'Driver assigned without hire agreement',
      weight: 15,
      verified: false
    });
  }

  // RULE L007: Unknown - escalate
  else {
    legal_result = 'escalate';
    legal_confidence = 30;
    legal_reason_code = 'L007';
    legal_reason_text = 'Insufficient data to determine liability - manual review required';
    evidence_tier = 5;
    escalation_required = true;
    recommended_action = 'investigate';
    formal_submission_safe = false;
  }

  return {
    legal_result,
    legal_confidence,
    legal_reason_code,
    legal_reason_text,
    evidence_tier,
    absorbed_by_policy,
    absorption_reason,
    formal_submission_safe,
    verification_required,
    escalation_required,
    hire_match_type: timeMatch?.match_type || 'none',
    hire_overlap_minutes: timeMatch?.overlap_minutes,
    matched_hire: timeMatch?.hire,
    late_return: lateReturn || undefined,
    recommended_action,
    evidence
  };
}

/**
 * Calculate risk score (0-100)
 * Weighted: Evidence 40%, Authority 25%, Stage 20%, Amount 15%
 */
export function calculateRiskScore(
  ticket: any,
  decision: Omit<LiabilityDecision, 'risk_score'>,
  _timeMatch: TimeMatch | null
): RiskScore {

  const factors: string[] = [];

  // 1. Evidence Score (40% weight)
  let evidence_score = 0;
  switch (decision.evidence_tier) {
    case 1: evidence_score = 95; break; // Strong evidence
    case 2: evidence_score = 80; break; // Good evidence
    case 3: evidence_score = 60; break; // Moderate evidence
    case 4: evidence_score = 40; break; // Weak evidence
    case 5: evidence_score = 20; break; // Very weak evidence
  }

  if (decision.evidence_tier <= 2) {
    factors.push('Strong evidence');
  } else if (decision.evidence_tier >= 4) {
    factors.push('Weak evidence');
  }

  // 2. Authority Score (25% weight)
  // Default to moderate risk, will be enhanced with authority profiles
  let authority_score = 50;

  if (ticket.issuing_authority?.toLowerCase().includes('westminster')) {
    authority_score = 70;
    factors.push('High-risk authority');
  } else if (ticket.issuing_authority?.toLowerCase().includes('parkingeye')) {
    authority_score = 85;
    factors.push('Aggressive private operator');
  }

  // 3. Stage Score (20% weight)
  let stage_score = 50;

  if (ticket.status === 'pending') {
    stage_score = 90; // Early stage, low risk
  } else if (ticket.status === 'appealed') {
    stage_score = 40; // Appeal stage, higher risk
  } else if (ticket.status === 'transferred') {
    stage_score = 60; // Transfer stage, moderate risk
  }

  // 4. Amount Score (15% weight)
  let amount_score = 50;
  const amount = parseFloat(ticket.amount) || 0;

  if (amount < 50) {
    amount_score = 80;
    factors.push('Low amount');
  } else if (amount > 150) {
    amount_score = 30;
    factors.push('High amount');
  }

  // Calculate weighted total
  const total = Math.round(
    (evidence_score * 0.40) +
    (authority_score * 0.25) +
    (stage_score * 0.20) +
    (amount_score * 0.15)
  );

  // Add decision-specific factors
  if (decision.verification_required) {
    factors.push('Verification required');
  }

  if (decision.escalation_required) {
    factors.push('Escalation required');
  }

  if (decision.late_return?.detected) {
    factors.push(`Late return: ${decision.late_return.severity}`);
  }

  return {
    total,
    evidence_score,
    authority_score,
    stage_score,
    amount_score,
    factors
  };
}

// =====================================================
// DATABASE FUNCTIONS
// =====================================================

async function getExistingDecision(ticketId: string): Promise<LiabilityDecision | null> {
  const { data, error } = await supabase
    .from('liability_decisions')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error || !data) {
    return null;
  }

  // Convert database record to LiabilityDecision
  return {
    legal_result: data.legal_result,
    legal_confidence: data.legal_confidence,
    legal_reason_code: data.legal_reason_code,
    legal_reason_text: data.legal_reason_text,
    evidence_tier: data.evidence_tier,
    commercial_result: data.commercial_result,
    commercial_reason: data.commercial_reason,
    absorbed_by_policy: data.absorbed_by_policy,
    absorption_reason: data.absorption_reason,
    absorption_amount: data.absorption_amount,
    formal_submission_safe: data.formal_submission_safe,
    verification_required: data.verification_required,
    escalation_required: data.escalation_required,
    hire_match_type: data.hire_match_type,
    hire_overlap_minutes: data.hire_overlap_minutes,
    matched_hire: data.matched_hire_id ? await getHire(data.matched_hire_id) : undefined,
    late_return: data.late_return_detected ? {
      detected: data.late_return_detected,
      minutes: data.late_return_minutes,
      severity: data.late_return_severity,
      grace_period_applied: data.late_return_minutes <= 60
    } : undefined,
    recommended_action: data.recommended_action,
    assigned_workflow: data.assigned_workflow,
    assigned_team: data.assigned_team,
    risk_score: {
      total: data.risk_score,
      evidence_score: 0,
      authority_score: 0,
      stage_score: 0,
      amount_score: 0,
      factors: data.risk_factors || []
    },
    evidence: data.evidence || []
  };
}

async function saveLiabilityDecision(
  ticketId: string,
  organisationId: string,
  decision: LiabilityDecision,
  audit?: AuditResult
): Promise<void> {

  const { error } = await supabase
    .from('liability_decisions')
    .insert({
      organisation_id: organisationId,
      ticket_id: ticketId,
      legal_result: decision.legal_result,
      legal_confidence: decision.legal_confidence,
      legal_reason_code: decision.legal_reason_code,
      legal_reason_text: decision.legal_reason_text,
      evidence_tier: decision.evidence_tier,
      commercial_result: decision.commercial_result,
      commercial_reason: decision.commercial_reason,
      absorbed_by_policy: decision.absorbed_by_policy,
      absorption_reason: decision.absorption_reason,
      absorption_amount: decision.absorption_amount,
      formal_submission_safe: decision.formal_submission_safe,
      verification_required: decision.verification_required,
      escalation_required: decision.escalation_required,
      matched_hire_id: decision.matched_hire?.id,
      hire_match_type: decision.hire_match_type,
      hire_overlap_minutes: decision.hire_overlap_minutes,
      late_return_detected: decision.late_return?.detected || false,
      late_return_minutes: decision.late_return?.minutes,
      late_return_severity: decision.late_return?.severity,
      recommended_action: decision.recommended_action,
      risk_score: decision.risk_score.total,
      risk_factors: decision.risk_score.factors,
      evidence: decision.evidence,
      // Store the audit result as JSONB
      audit_result: audit ? {
        hireDetected: audit.hireDetected,
        hireId: audit.hireId,
        matchType: audit.matchType,
        agreementPresent: audit.agreementPresent,
        agreementSigned: audit.agreementSigned,
        liabilityClausePresent: audit.liabilityClausePresent,
        coversContravention: audit.coversContravention,
        lateReturnStatus: audit.lateReturnStatus,
        evidenceTier: audit.evidenceTier,
        confidenceScore: audit.confidenceScore,
        autoAssignable: audit.autoAssignable,
        reason: audit.reason,
        blockingFactors: audit.blockingFactors,
        warningFactors: audit.warningFactors || []
      } : null,
      calculated_by: (await supabase.auth.getUser()).data.user?.id
    });

  if (error) {
    console.error('Error saving liability decision:', error);
    throw error;
  }

  // Update ticket with liability result AND audit result
  await supabase
    .from('tickets')
    .update({
      liability_result: decision.legal_result,
      liability_confidence: decision.legal_confidence,
      assigned_workflow: decision.assigned_workflow,
      // Store audit result on ticket for quick access
      audit_result: audit ? {
        confidenceScore: audit.confidenceScore,
        autoAssignable: audit.autoAssignable,
        matchType: audit.matchType,
        evidenceTier: audit.evidenceTier,
        blockingFactors: audit.blockingFactors
      } : null
    })
    .eq('id', ticketId);
}

async function getHire(hireId: string): Promise<any> {
  const { data } = await supabase
    .from('hires')
    .select('*')
    .eq('id', hireId)
    .single();

  return data;
}

async function getDriver(driverId: string): Promise<any> {
  const { data } = await supabase
    .from('drivers')
    .select('*')
    .eq('id', driverId)
    .single();

  return data;
}

async function getAllHiresForVehicle(vehicleId: string, organisationId: string): Promise<any[]> {
  const { data } = await supabase
    .from('hires')
    .select('*')
    .eq('vehicle_id', vehicleId)
    .eq('organisation_id', organisationId)
    .order('start_date', { ascending: false });

  return data || [];
}

// =====================================================
// GUARDRAILS & AUTO-ASSIGNMENT
// =====================================================

export interface GuardrailResult {
  auto_process_allowed: boolean;
  blocking_factors: string[];
  warning_factors: string[];
  confidence_threshold_met: boolean;
  evidence_threshold_met: boolean;
  risk_threshold_met: boolean;
}

/**
 * Check guardrails for auto-processing
 */
export function checkGuardrails(
  decision: LiabilityDecision,
  ticket: any
): GuardrailResult {

  const blocking_factors: string[] = [];
  const warning_factors: string[] = [];

  // 1. Verification required = BLOCK
  if (decision.verification_required) {
    blocking_factors.push('Verification required');
  }

  // 2. Escalation required = BLOCK
  if (decision.escalation_required) {
    blocking_factors.push('Escalation required');
  }

  // 3. Not safe for formal submission = BLOCK
  if (!decision.formal_submission_safe) {
    blocking_factors.push('Not safe for formal submission');
  }

  // 4. Confidence threshold (must be >= 80%)
  const confidence_threshold_met = decision.legal_confidence >= 80;
  if (!confidence_threshold_met) {
    blocking_factors.push(`Low confidence: ${decision.legal_confidence}%`);
  }

  // 5. Evidence threshold (must be tier 1 or 2)
  const evidence_threshold_met = decision.evidence_tier <= 2;
  if (!evidence_threshold_met) {
    warning_factors.push(`Weak evidence: Tier ${decision.evidence_tier}`);
  }

  // 6. Risk threshold (must be >= 60)
  const risk_threshold_met = decision.risk_score.total >= 60;
  if (!risk_threshold_met) {
    warning_factors.push(`High risk: ${decision.risk_score.total}/100`);
  }

  // 7. High amount warning (>£150)
  const amount = parseFloat(ticket.amount) || 0;
  if (amount > 150) {
    warning_factors.push(`High amount: £${amount}`);
  }

  // 8. Late return severity
  if (decision.late_return?.detected && decision.late_return.severity !== 'minor') {
    warning_factors.push(`Late return: ${decision.late_return.severity}`);
  }

  const auto_process_allowed = blocking_factors.length === 0;

  return {
    auto_process_allowed,
    blocking_factors,
    warning_factors,
    confidence_threshold_met,
    evidence_threshold_met,
    risk_threshold_met
  };
}

/**
 * Determine workflow and team assignment
 */
export function assignWorkflow(
  decision: LiabilityDecision,
  guardrails: GuardrailResult
): { workflow: string; team: string } {

  // If escalation required, assign to admin team
  if (decision.escalation_required) {
    return {
      workflow: 'escalation',
      team: 'admin'
    };
  }

  // If verification required, assign to verification team
  if (decision.verification_required) {
    return {
      workflow: 'verification',
      team: 'operations'
    };
  }

  // Based on recommended action
  switch (decision.recommended_action) {
    case 'transfer':
      return {
        workflow: 'liability_transfer',
        team: guardrails.auto_process_allowed ? 'auto' : 'operations'
      };

    case 'appeal':
      return {
        workflow: 'appeal',
        team: guardrails.auto_process_allowed ? 'auto' : 'operations'
      };

    case 'pay':
      return {
        workflow: 'payment',
        team: 'finance'
      };

    case 'investigate':
      return {
        workflow: 'investigation',
        team: 'admin'
      };

    case 'review':
    default:
      return {
        workflow: 'manual_review',
        team: 'operations'
      };
  }
}

/**
 * Calculate deadline dates
 */
export function calculateDeadlines(ticket: any): {
  deadline_date: Date | null;
  discount_deadline_date: Date | null;
} {

  if (!ticket.issued_date) {
    return {
      deadline_date: null,
      discount_deadline_date: null
    };
  }

  const issuedDate = new Date(ticket.issued_date);

  // Standard deadlines for UK PCNs
  // Discount period: 14 days
  // Full payment period: 28 days

  const discount_deadline_date = new Date(issuedDate);
  discount_deadline_date.setDate(discount_deadline_date.getDate() + 14);

  const deadline_date = new Date(issuedDate);
  deadline_date.setDate(deadline_date.getDate() + 28);

  return {
    deadline_date,
    discount_deadline_date
  };
}

// =====================================================
// PUBLIC API
// =====================================================

/**
 * Complete liability analysis with guardrails and assignment
 */
export async function analyzeLiability(
  ticketId: string,
  organisationId: string,
  forceRecalculate: boolean = false
): Promise<{
  decision: LiabilityDecision;
  guardrails: GuardrailResult;
  assignment: { workflow: string; team: string };
  deadlines: { deadline_date: Date | null; discount_deadline_date: Date | null };
}> {

  // 1. Determine liability
  const liabilityResult = await determineLiability(ticketId, organisationId, forceRecalculate);
  const decision = liabilityResult.decision;

  // 2. Fetch ticket for guardrails
  const { data: ticket } = await supabase
    .from('tickets')
    .select('*')
    .eq('id', ticketId)
    .single();

  if (!ticket) {
    throw new Error('Ticket not found');
  }

  // 3. Check guardrails
  const guardrails = checkGuardrails(decision, ticket);

  // 4. Assign workflow and team
  const assignment = assignWorkflow(decision, guardrails);

  // 5. Calculate deadlines
  const deadlines = calculateDeadlines(ticket);

  // 6. Update ticket with assignment and deadlines
  await supabase
    .from('tickets')
    .update({
      assigned_workflow: assignment.workflow,
      assigned_team: assignment.team,
      deadline_date: deadlines.deadline_date,
      discount_deadline_date: deadlines.discount_deadline_date,
      auto_processed: guardrails.auto_process_allowed
    })
    .eq('id', ticketId);

  return {
    decision,
    guardrails,
    assignment,
    deadlines
  };
}


